import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api, formatSAR } from "@/lib/api";
import { toast } from "sonner";
import { X, AlertTriangle, Plus, RefreshCw, Loader2 } from "lucide-react";

/**
 * Modal for cancelling or replacing a transaction.
 * Props:
 *   tx           — the transaction object
 *   mode         — "cancel" | "change"
 *   onClose()
 *   onDone(refund, mode)  — fired after success; for "change" parent should
 *                            open a new tx form pre-filled with customer_phone
 */
export default function CancelTransactionModal({ tx, mode = "cancel", onClose, onDone }) {
  const { t, lang } = useLang();
  const [reasons, setReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [adding, setAdding] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cancel-reasons").then((r) => {
      setReasons(r.data);
      // Auto-select "Ganti layanan" for change mode
      if (mode === "change") {
        const m = r.data.find((x) => x.label.toLowerCase().includes("ganti"));
        if (m) setSelectedReasonId(m.id);
      }
    });
  }, [mode]);

  const reasonLabel = (r) => (lang === "ar" && r.label_ar ? r.label_ar : r.label);

  async function addCustom() {
    const label = customReason.trim();
    if (!label) return;
    setBusy(true);
    try {
      const { data } = await api.post("/cancel-reasons", { label });
      setReasons((prev) => prev.some((r) => r.id === data.id) ? prev : [...prev, data]);
      setSelectedReasonId(data.id);
      setCustomReason("");
      setAdding(false);
      toast.success("Alasan ditambahkan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  }

  async function submit() {
    const selected = reasons.find((r) => r.id === selectedReasonId);
    if (!selected) return toast.error(t("cancel.selectReason"));
    setBusy(true);
    try {
      const { data: refund } = await api.post(`/transactions/${tx.id}/cancel`, {
        reason: selected.label,
        reason_id: selected.id,
        notes,
        refund_type: mode,
      });
      toast.success(mode === "change" ? t("cancel.changeSuccess") : t("cancel.success"));
      onDone?.(refund, mode);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  }

  const title = mode === "change" ? t("cancel.changeModalTitle") : t("cancel.modalTitle");

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="card-luxury w-full max-w-md max-h-[90vh] overflow-y-auto scrollbar-thin" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-heading flex items-center gap-2">
              {mode === "change" ? (
                <RefreshCw className="h-5 w-5 text-leaf" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-400" />
              )}
              {title}
            </h2>
            <button onClick={onClose} data-testid="cancel-modal-close"><X className="h-5 w-5" /></button>
          </div>

          {/* Transaction summary */}
          <div className="bg-charcoal-900/60 border border-gold/15 rounded-lg p-3 text-sm mb-5">
            <div className="text-xs text-foreground/50 uppercase tracking-[0.2em] mb-2">Detail Transaksi</div>
            <div>📞 {tx.customer_phone}</div>
            <div className="text-foreground/70">{tx.items.map((i) => i.service_name_id).join(", ")}</div>
            <div className="mt-2 pt-2 border-t border-gold/10 flex justify-between">
              <span className="text-foreground/60">{t("cancel.refundAmount")}</span>
              <span className="text-gold font-heading">{formatSAR(tx.total)}</span>
            </div>
          </div>

          {/* Reason picker */}
          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">
              {t("cancel.selectReason")} *
            </label>
            <div className="space-y-1.5">
              {reasons.map((r) => (
                <label
                  key={r.id}
                  data-testid={`reason-option-${r.id}`}
                  className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition ${
                    selectedReasonId === r.id
                      ? "border-gold bg-gold/10"
                      : "border-charcoal-600 hover:border-gold/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.id}
                    checked={selectedReasonId === r.id}
                    onChange={() => setSelectedReasonId(r.id)}
                    className="mt-1 accent-gold"
                  />
                  <div className="flex-1 text-sm">
                    {reasonLabel(r)}
                    {r.created_by_name && r.created_by_name !== "System" && (
                      <div className="text-[10px] text-foreground/40 mt-0.5">+ oleh {r.created_by_name}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Add custom reason */}
            {!adding ? (
              <button
                onClick={() => setAdding(true)}
                data-testid="add-reason-btn"
                className="mt-3 text-sm text-gold hover:text-gold-light inline-flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> {t("cancel.addReason")}
              </button>
            ) : (
              <div className="mt-3 flex gap-2">
                <input
                  data-testid="new-reason-input"
                  autoFocus
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCustom()}
                  placeholder={t("cancel.newReasonPlaceholder")}
                  className="flex-1 bg-charcoal-900 border border-charcoal-600 focus:border-gold rounded-md px-3 py-2 text-sm outline-none"
                />
                <button onClick={addCustom} disabled={busy} data-testid="save-reason-btn" className="btn-gold text-sm py-2 px-3 disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "OK"}
                </button>
                <button onClick={() => { setAdding(false); setCustomReason(""); }} className="btn-outline-gold text-sm py-2 px-3">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mb-5">
            <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">{t("cancel.notes")}</label>
            <textarea
              data-testid="cancel-notes"
              rows="3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-charcoal-900 border border-charcoal-600 focus:border-gold rounded-lg px-3 py-2 text-sm outline-none"
            />
          </div>

          {/* Confirm buttons */}
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-outline-gold flex-1 justify-center">
              {t("common.cancel")}
            </button>
            <button
              onClick={submit}
              disabled={busy || !selectedReasonId}
              data-testid="cancel-confirm-btn"
              className={`flex-1 justify-center inline-flex items-center gap-2 py-2.5 rounded-full font-semibold transition disabled:opacity-50 ${
                mode === "change"
                  ? "bg-leaf hover:bg-leaf-light text-charcoal-900"
                  : "bg-red-500 hover:bg-red-400 text-white"
              }`}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "change" ? t("cancel.changeConfirm") : t("cancel.confirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
